import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { getOperationById } from '../services/operationService';
import { Operation } from '../types/operation';
import { useCart } from '../contexts/CartContext';
import { useFieldAuth } from '../contexts/FieldContext';
import { CartItem } from '../types/cart';
import YouTubePlayer from '../components/common/YouTubePlayer';
import ImageModal from '../components/common/ImageModal';


const Container = styled.div`
  padding: 100px 20px 40px;
  max-width: 1200px;
  margin: 0 auto;
  color: #e0e0e0;
  
  @media (max-width: 768px) {
    padding: 80px 15px 20px;
  }
`;

const BreadcrumbNav = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  color: #a0a9b8;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  
  a {
    color: #00d4ff;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: #ffffff;
    }
  }
  
  span {
    margin: 0 8px;
    color: #666;
  }
`;

const MissionTitle = styled.h1`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 2.5rem;
  color: #00d4ff;
  text-align: center;
  margin-bottom: 20px;
  font-weight: 700;
  text-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const MissionId = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 5px;
  }
`;

const StatusBar = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 15px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  color: #a0a9b8;
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const MainContent = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const MissionMap = styled.div`
  flex: 1;
  height: 400px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const MapTarget = styled.div<{ top: string; left: string }>`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  width: 90px;
  height: 90px;
  border: 2px dashed rgba(0, 212, 255, 0.6);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #00d4ff;
  background: rgba(0, 212, 255, 0.1);
  backdrop-filter: blur(2px);
`;

const SpecsPanel = styled.div`
  width: 320px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 25px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SpecsTitle = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 1.3rem;
  color: #00d4ff;
  text-align: center;
  margin-bottom: 20px;
  font-weight: 600;
`;

const SpecsTable = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
  margin-bottom: 18px;
`;

const SpecLabel = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  color: #a0a9b8;
  text-align: right;
  padding-right: 12px;
  border-right: 1px solid rgba(0, 212, 255, 0.2);
  font-weight: 500;
`;

const SpecValue = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  color: #00d4ff;
  padding-left: 12px;
  font-weight: 500;
`;

const PriceTag = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 1.8rem;
  color: #00d4ff;
  text-align: center;
  margin-bottom: 15px;
  font-weight: 700;
`;

const StockStatus = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  color: #00ff88;
  text-align: center;
  background: rgba(0, 255, 136, 0.1);
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 255, 136, 0.3);
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const MissionBriefing = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 40px;
`;

const BriefingTitle = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 1.5rem;
  color: #00d4ff;
  text-align: center;
  margin-bottom: 25px;
  font-weight: 600;
`;

const ClassifiedHeader = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  color: #a0a9b8;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
`;

const BriefingText = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 15px;
  color: #e0e0e0;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const WarningText = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  color: #ff6b6b;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: rgba(255, 107, 107, 0.1);
  border-left: 3px solid #ff6b6b;
  border-radius: 4px;
`;

const EquipmentText = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  color: #00d4ff;
  background: rgba(0, 212, 255, 0.1);
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid rgba(0, 212, 255, 0.2);
`;

const AcquisitionSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 40px;
`;

const AcquisitionTitle = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 1.5rem;
  color: #00d4ff;
  text-align: center;
  margin-bottom: 25px;
  font-weight: 600;
`;

const CounterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 9px;
  margin-bottom: 18px;
`;

const CounterLabel = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  color: #a0a9b8;
  font-weight: 500;
`;

const CounterButton = styled.button`
  width: 40px;
  height: 40px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #00d4ff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
    transform: scale(1.05);
  }
`;

const CounterValue = styled.div`
  width: 80px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 6px;
  color: #00d4ff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddToInventoryButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 600;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background: linear-gradient(135deg, #00a8cc, #007799);
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 212, 255, 0.4);
  }
`;

const RelatedSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 40px;
`;

const RelatedTitle = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 1.5rem;
  color: #00d4ff;
  text-align: center;
  margin-bottom: 25px;
  font-weight: 600;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedMission = styled(Link)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 20px;
  text-decoration: none;
  transition: all 0.3s ease;
  text-align: center;
  display: block;
  
  &:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: rgba(0, 212, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.2);
  }
`;

const RelatedMissionTitle = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 16px;
  color: #00d4ff;
  margin-bottom: 8px;
  font-weight: 600;
`;

const RelatedMissionDetails = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  color: #a0a9b8;
`;

const FooterInfo = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  color: #666;
  text-align: center;
  border-top: 1px solid rgba(0, 212, 255, 0.2);
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ImagesSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 40px;
`;

const ImagesTitle = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 1.5rem;
  color: #00d4ff;
  text-align: center;
  margin-bottom: 25px;
  font-weight: 600;
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const ImageItem = styled.div`
  height: 200px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  
  &:hover {
    border-color: rgba(0, 212, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.2);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  
  .image-placeholder {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    color: #a0a9b8;
    text-align: center;
  }
`;




const NoImagesMessage = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  color: #a0a9b8;
  text-align: center;
  padding: 30px;
  border: 1px dashed rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
`;

const VideoNavigationContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  z-index: 10;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 6px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  max-width: calc(100% - 40px);
  backdrop-filter: none;
`;

const VideoNavButton = styled.button<{ active: boolean }>`
  width: 24px;
  height: 24px;
  background: ${props => props.active ? '#00d4ff' : 'rgba(0, 0, 0, 0.6)'};
  border: 1px solid ${props => props.active ? '#00d4ff' : '#666'};
  border-radius: 4px;
  color: ${props => props.active ? '#000' : '#00d4ff'};
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#00b8e6' : 'rgba(0, 212, 255, 0.2)'};
    transform: scale(1.1);
  }
`;

const MissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [mission, setMission] = useState<Operation | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  const { isFieldAuthenticated } = useFieldAuth();

  const [showFullImage, setShowFullImage] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const fixImageUrl = (url: string): string => {
    if (!url) return url;
    return url.replace('.net.er/', '.net/');
  };

  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index));
  };

  useEffect(() => {
    const fetchMission = async () => {
      try {
        if (!id) return;
        const data: any = await getOperationById(id);
        
        const fixedImages = data.images?.map((url: string) => fixImageUrl(url)) || [];
        
        const enhancedData = {
          ...data,
          images: fixedImages,
          imageUrl: data.imageUrl ? fixImageUrl(data.imageUrl) : data.imageUrl,
          gameType: data.gameType || 'AIRSOFT',
          difficulty: data.difficulty || 'Medium',
          difficultyLevel: typeof data.difficulty === 'string' ? 
            data.difficulty === 'Easy' ? 1 : 
            data.difficulty === 'Medium' ? 2 : 
            data.difficulty === 'Hard' ? 3 : 
            data.difficulty === 'Expert' ? 4 : 2 
            : 2,
          missionId: data.missionId || data.tags?.find((tag: string) => tag.startsWith('MISSION-'))?.replace('MISSION-', '') || 'ASM-001',
          duration: data.duration || 120,
          unitSize: `${data.playerCountMin || 6}-${data.playerCountMax || 12} OPERATIVES`,
          terrain: 'URBAN',
          successRate: '43%',
          stockStatus: 'AVAILABLE',
          clearanceLevel: '3',
          priority: 'ALPHA',
          lastUpdated: data.updatedAt ? new Date(data.updatedAt).toLocaleDateString() : '05-03-2025',
          briefing: data.description || 'Infiltrate hostile territory to extract high-value intelligence data while remaining undetected. Two teams will operate in tandem - ALPHA TEAM focused on distraction protocols, while BRAVO TEAM conducts the primary extraction. Advanced stealth tactics required.',
          warning: 'WARNING: Hostile forces equipped with motion sensors and thermal imaging.',
          equipment: 'Standard tactical gear, night vision capability, silent markers',
          relatedMissions: [
            { id: '7', name: 'SHADOW HUNTER', gameType: 'AIRSOFT', difficulty: 3, price: 3200 },
            { id: '8', name: 'NIGHT OPS', gameType: 'AIRSOFT', difficulty: 2, price: 2900 },
            { id: '9', name: 'SILENT STRIKE', gameType: 'AIRSOFT', difficulty: 4, price: 4100 }
          ]
        };
        
        setMission(enhancedData);
      } catch (err) {
        console.error('Error loading mission data:', err);
        setError('Error loading mission data');
      } finally {
        setLoading(false);
      }
    };

    fetchMission();
  }, [id]);

  const handleAddToCart = () => {
    if (mission) {
      const cartItem: CartItem = {
        id: mission.id,
        name: mission.title || mission.name || 'Mission',
        description: mission.description,
        price: mission.price,
        quantity: quantity,
        image: mission.images?.[0]
      };
      
      addItem(cartItem);
      navigate('/cart');
    }
  };

  const incrementQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    setQuantity(prev => (prev > 1 ? prev - 1 : 1));
  };

  const openFullImage = (index: number) => {
    setCurrentImageIndex(index);
    setShowFullImage(true);
  };
  
  const closeFullImage = () => {
    setShowFullImage(false);
  };
  
  const handleImageNavigation = (newIndex: number) => {
    setCurrentImageIndex(newIndex);
  };


  if (loading) {
    return (
      <Container>
        <MissionTitle>LOADING MISSION DATA</MissionTitle>
      </Container>
    );
  }

  if (error || !mission) {
    return (
      <Container>
        <MissionTitle>ACCESS ERROR</MissionTitle>
        <BriefingText style={{ textAlign: 'center' }}>
          {error || 'Mission not found. Check your access level.'}
        </BriefingText>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <BreadcrumbNav>
          <Link to="/operations">OPERATIONS DATABASE</Link>
          <span>&gt;</span>
          <Link to={`/operations?type=${mission.gameType}`}>{mission.gameType}</Link>
          <span>&gt;</span>
          <Link to={`/operations?difficulty=${mission.difficulty}`}>DIFFICULTY {mission.difficulty}</Link>
          <span>&gt;</span>
          {mission.name}
        </BreadcrumbNav>
        
        <MissionTitle>{mission.name}</MissionTitle>
        <MissionId>
          MISSION ID: {mission.missionId} • CLEARANCE LEVEL: {mission.clearanceLevel} • PRIORITY: {mission.priority}
        </MissionId>
        
        <StatusBar>
          <span>DATA ASSESSMENT: CLASSIFIED</span>
          <span>SITREP: PENDING</span>
          <span>LAST UPDATED: {mission.lastUpdated}</span>
        </StatusBar>
        
        <MainContent>
          <MissionMap>
            {mission.videos && mission.videos.length > 0 ? (
              <div style={{ 
                position: 'relative', 
                width: '100%', 
                height: '100%',
                overflow: 'hidden'
              }}>
                <YouTubePlayer 
                  videoUrl={mission.videos[currentVideoIndex]} 
                  autoplay={false}
                />
                {mission.videos.length > 1 && (
                  <VideoNavigationContainer>
                    {mission.videos.map((_, index) => (
                      <VideoNavButton
                        key={index}
                        active={index === currentVideoIndex}
                        onClick={() => setCurrentVideoIndex(index)}
                      >
                        {index + 1}
                      </VideoNavButton>
                    ))}
                  </VideoNavigationContainer>
                )}
              </div>
            ) : mission.images && mission.images.length > 0 ? (
              <div style={{ 
                width: '100%', 
                height: '100%', 
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Размытый фоновый слой */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${fixImageUrl(mission.images[0])})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(8px)',
                  transform: 'scale(1.1)'
                }} />
                
                {/* Зеленый оверлей */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 25, 0, 0.3)'
                }} />
                
                {/* Основное четкое изображение */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2
                }}>
                  <img 
                    src={fixImageUrl(mission.images[0])} 
                    alt="Mission preview"
                    style={{
                      maxWidth: '95%',
                      maxHeight: '95%',
                      objectFit: 'contain',
                      cursor: 'pointer',
                      filter: 'none'
                    }}
                    onClick={() => openFullImage(0)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div style="
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 100%;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
                            font-size: 14px;
                            color: #a0a9b8;
                            text-align: center;
                          ">
                            ИЗОБРАЖЕНИЕ НЕДОСТУПНО<br />
                            ПРОВЕРЬТЕ СОЕДИНЕНИЕ
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              </div>
            ) : (
              <>
                <MapTarget top="20%" left="15%">TARGET-A</MapTarget>
                <MapTarget top="55%" left="65%">TARGET-B</MapTarget>
              </>
            )}
          </MissionMap>
          
          <SpecsPanel>
            <SpecsTitle>MISSION SPECS</SpecsTitle>
            
            <SpecsTable>
              <SpecLabel>TYPE:</SpecLabel>
              <SpecValue>{mission.gameType}</SpecValue>
              
              <SpecLabel>DIFFICULTY:</SpecLabel>
              <SpecValue>LEVEL {mission.difficultyLevel || 
                (typeof mission.difficulty === 'string' ? 
                  mission.difficulty === 'Easy' ? 1 : 
                  mission.difficulty === 'Medium' ? 2 : 
                  mission.difficulty === 'Hard' ? 3 : 
                  mission.difficulty === 'Expert' ? 4 : 2 
                : mission.difficulty)
              }</SpecValue>
              
              <SpecLabel>DURATION:</SpecLabel>
              <SpecValue>{mission.duration} MIN</SpecValue>
              
              <SpecLabel>UNIT SIZE:</SpecLabel>
              <SpecValue>{mission.unitSize}</SpecValue>
              
              <SpecLabel>TERRAIN:</SpecLabel>
              <SpecValue>{mission.terrain}</SpecValue>
              
              <SpecLabel>SUCCESS RATE:</SpecLabel>
              <SpecValue>{mission.successRate}</SpecValue>
            </SpecsTable>
            
            {isFieldAuthenticated && <PriceTag>{mission.price} $</PriceTag>}
            <StockStatus>STOCK STATUS: {mission.stockStatus}</StockStatus>
          </SpecsPanel>
        </MainContent>
        
        <MissionBriefing>
          <BriefingTitle>MISSION BRIEFING</BriefingTitle>
          <ClassifiedHeader>CLASSIFIED LEVEL {mission.clearanceLevel} // EYES ONLY</ClassifiedHeader>
          
          <BriefingText>
            OBJECTIVE: {mission.briefing}
          </BriefingText>
          
          <WarningText>
            {mission.warning}
          </WarningText>
          
          <EquipmentText>
            REQUIRED EQUIPMENT: {mission.equipment}
          </EquipmentText>
        </MissionBriefing>
        
        {/* Добавляем секцию галереи изображений */}
        <ImagesSection>
          <ImagesTitle>MISSION IMAGES</ImagesTitle>
          
          {mission.images && mission.images.length > 0 ? (
            <ImagesGrid>
              {mission.images.map((image, index) => (
                <ImageItem 
                  key={index}
                  onClick={() => openFullImage(index)}
                >
                  {imageErrors.has(index) ? (
                    <div className="image-placeholder">
                      IMAGE UNAVAILABLE<br />
                      CHECK CONNECTION
                    </div>
                  ) : (
                    <img 
                      src={image} 
                      alt={`Mission image ${index + 1}`}
                      onError={() => handleImageError(index)}
                    />
                  )}
                </ImageItem>
              ))}
            </ImagesGrid>
          ) : (
            <NoImagesMessage>
              NO AVAILABLE IMAGERY FOR THIS OPERATION
            </NoImagesMessage>
          )}
        </ImagesSection>
        
        {isFieldAuthenticated && (
          <AcquisitionSection>
            <AcquisitionTitle>MISSION ACQUISITION</AcquisitionTitle>

            <CounterContainer>
              <CounterLabel>MISSION COUNT:</CounterLabel>
              <CounterButton onClick={decrementQuantity}>-</CounterButton>
              <CounterValue>{quantity}</CounterValue>
              <CounterButton onClick={incrementQuantity}>+</CounterButton>
            </CounterContainer>

            <AddToInventoryButton onClick={handleAddToCart}>
              ADD TO INVENTORY
            </AddToInventoryButton>
          </AcquisitionSection>
        )}
        
        <RelatedSection>
          <RelatedTitle>RELATED OPERATIONS</RelatedTitle>
          
          <RelatedGrid>
            {mission.relatedMissions?.map((relatedMission) => (
              <RelatedMission key={relatedMission.id} to={`/operations/${relatedMission.id}`}>
                <RelatedMissionTitle>{relatedMission.name}</RelatedMissionTitle>
                <RelatedMissionDetails>
                  {relatedMission.gameType} • LVL {relatedMission.difficulty} • {relatedMission.price} ₽
                </RelatedMissionDetails>
              </RelatedMission>
            ))}
          </RelatedGrid>
        </RelatedSection>
        
        <FooterInfo>
          <span>DATABASE ACCESS: UNRESTRICTED</span>
          <span>CONNECTION: SECURE</span>
          <span>RECOMMENDED MISSION COMPATIBILITY: 87%</span>
        </FooterInfo>
      </Container>
      
      {/* Модальное окно для галереи изображений */}
      <ImageModal
        isOpen={showFullImage}
        imageUrl={mission?.images?.[currentImageIndex] || ''}
        imageAlt={`Mission image ${currentImageIndex + 1}`}
        title={mission?.name}
        onClose={closeFullImage}
        images={mission?.images}
        currentIndex={currentImageIndex}
        onNavigate={handleImageNavigation}
      />
    </>
  );
};

export default MissionDetailPage; 